// Import the OnboardingApiData type from the existing model
import { isDefined } from "@clipboard-health/util-ts";
import { put } from "@src/appV2/api";
import {
  isCurrentFirebaseTokenTooOld,
  signOutIfTokenTooOld,
} from "@src/appV2/Auth/cognito/firebaseTokenUtils";
import { exchangeCognitoTokenForFirebase } from "@src/appV2/Auth/cognito/tokenExchange";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { fullAddressSchema, locationSchema } from "@src/appV2/lib/address/types";
import { logError } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
// eslint-disable-next-line no-restricted-imports
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { z } from "zod";

import { ONBOARDING_STAGES } from "../util/constants";

const UPDATE_ONBOARDING_WORKER_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/onboarding/updateAgent`;

// add more if migrating any other stage to appV2;
const workerStage = z.nativeEnum(ONBOARDING_STAGES);

const addressSchema = fullAddressSchema
  .extend({
    isApproximateMsa: z.boolean(),
  })
  .partial()
  .strict();

export type Address = z.infer<typeof addressSchema>;
export type Location = z.infer<typeof locationSchema>;

const baseSchema = z.object({
  // We want to execute some condition logic in backend for new revamped sign flow, so we send this flag
  // Once we fully migrate to new revamped flow we can remove this code
  signupFlowVersion: z.literal("V2").optional(),
});

const updateOnboardingWorkerRequestSchema = z.union([
  // there is a 'stage' being 'updateStageOnly' in the backend that just sets the 'newStage' of the
  // worker without doing bunch of other things and validations that are irrelevant to the new flow anyways
  baseSchema.extend({
    stage: z.literal("updateStageOnly"),
    newStage: workerStage,
  }),
  baseSchema.extend({
    stage: z.literal(ONBOARDING_STAGES.EMAIL),
    email: z.string().email(),
  }),
  baseSchema.extend({
    stage: z.literal(ONBOARDING_STAGES.NAME),
    firstName: z.string(),
    middleName: z.string().optional(),
    lastName: z.string(),
    requestManualOigValidation: z.boolean().optional(),
  }),
  baseSchema.extend({
    stage: z.literal(ONBOARDING_STAGES.ADDRESS),
    address: addressSchema,
    location: locationSchema,
    tmz: z.string().optional(),
  }),
]);

const updateOnboardingWorkerResponseSchema = z.object({
  success: z.boolean(),
  agent: z
    .object({
      _id: z.string(),
      userId: z.string(),
      email: z.string().email().nullable().optional(),
    })
    .optional(),
});

export type UpdateOnboardingWorkerResponse = z.infer<typeof updateOnboardingWorkerResponseSchema>;

type UpdateOnboardingWorkerRequest = z.infer<typeof updateOnboardingWorkerRequestSchema>;

async function validateTokenForEmailStage(stage: string): Promise<void> {
  if (stage !== ONBOARDING_STAGES.EMAIL) {
    return;
  }

  const isTokenTooOld = await isCurrentFirebaseTokenTooOld();

  if (isTokenTooOld === true) {
    await signOutIfTokenTooOld();
    throw new Error("Token is too old. User has been signed out. Please log in again.");
  }
}

// Cognito onboarding can be toggled during the onboarding process.
// Always prioritize the feature flag, but if the configured token type is
// unavailable, gracefully fall back to any present authentication token
// This ensures robust authentication regardless of graduation path changes.
async function makeUpdateRequest(
  updateData: UpdateOnboardingWorkerRequest,
  isCognitoOnboardingEnabled: boolean
): Promise<UpdateOnboardingWorkerResponse> {
  const firebaseToken = await getFirebaseSingleton().currentUser?.getIdToken();
  const cognitoToken = await getCognitoSingleton().getIdToken();
  const isFirebaseTokenDefined = isDefined(firebaseToken);
  const isCognitoTokenDefined = isDefined(cognitoToken);

  // At least one authentication token must be available.
  if (!isCognitoTokenDefined && !isFirebaseTokenDefined) {
    throw new Error("No authentication token available");
  }

  if ((isCognitoOnboardingEnabled && isCognitoTokenDefined) || !isFirebaseTokenDefined) {
    const axiosResponse = await axios.put(UPDATE_ONBOARDING_WORKER_URL, updateData, {
      headers: {
        Authorization: `Bearer ${cognitoToken}`,
        "Content-Type": "application/json",
      },
    });

    return updateOnboardingWorkerResponseSchema.parse(axiosResponse.data);
  }

  const response = await put({
    url: UPDATE_ONBOARDING_WORKER_URL,
    data: updateData,
    responseSchema: updateOnboardingWorkerResponseSchema,
    requestSchema: updateOnboardingWorkerRequestSchema,
  });

  return response.data;
}

export function useUpdateOnboardingWorker(
  options: UseMutationOptions<
    UpdateOnboardingWorkerResponse,
    AxiosError,
    UpdateOnboardingWorkerRequest
  > = {}
) {
  const isCognitoOnboardingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_ONBOARDING, {
    defaultValue: false,
  });

  return useMutation({
    mutationKey: [UPDATE_ONBOARDING_WORKER_URL],
    mutationFn: async (updateData: UpdateOnboardingWorkerRequest) => {
      await validateTokenForEmailStage(updateData.stage);
      return await makeUpdateRequest(updateData, isCognitoOnboardingEnabled);
    },
    onSuccess: async (data, variables) => {
      // Refresh Cognito token after successful updates to email, name, or address
      // This ensures the token contains the newly updated user information
      const shouldRefreshToken =
        variables.stage === ONBOARDING_STAGES.EMAIL ||
        variables.stage === ONBOARDING_STAGES.NAME ||
        variables.stage === ONBOARDING_STAGES.ADDRESS;

      // Force refresh Cognito token to get updated user attributes and a
      // Firebase token is created, so we can toggle the FF in the middle with
      // no issues.
      if (shouldRefreshToken) {
        try {
          await getCognitoSingleton().getIdToken(true);
        } catch (error) {
          // Log error but don't fail the mutation - token refresh failure is non-critical
          // The token will be refreshed on the next API call
          logError(APP_V2_APP_EVENTS.FAILED_TO_REFRESH_COGNITO_TOKEN, {
            error,
            metadata: {
              stage: variables.stage,
              context: "onboarding_update_agent_success",
            },
          });
        }

        try {
          const firebaseToken = await exchangeCognitoTokenForFirebase();
          await getFirebaseSingleton().signInWithCustomToken(firebaseToken);
        } catch (error) {
          logError(APP_V2_APP_EVENTS.FAILED_TO_REFRESH_FIREBASE_TOKEN, {
            error,
            metadata: {
              stage: variables.stage,
              context: "onboarding_update_agent_success",
            },
          });
        }
      }

      // Call user's onSuccess callback if provided
      options.onSuccess?.(data, variables, undefined);
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PATCH_WORKER_ONBOARDING_FAILURE,
    },
    ...options,
  });
}
